/**
 * 69.10, the orbit-to-base leg — step 1, the run and the pose (DECISIONS 224, 227; the owner: "resupply from orbit … the actual
 * orbit to surface landing could be interesting, want to make it dialed and semi realistic", "profile should aim towards
 * realism, climb out seems like it'll be the least realistic … own strip"; BUILDLIST 69.10). The lighter (67.75's lifting
 * body A: four frames inside, rolled out down a rear ramp) comes down from the ship in orbit to the side's **strip** — its
 * own levelled ground behind the motor pool, on the side away from the enemy (`lighterStripSite`, placed at the start with
 * `GameConfig.lighters`) — and goes back up. A run is a **path of the tick** like the dropship's flight: `callLighter`
 * plans its ticks from LIGHTER's numbers, `lighterPose` is a pure function of the run and the tick (position, heading,
 * pitch, the fans' and bells' shares, the ramp, the frames aboard), so replays, the goldens and the look agree.
 *
 * **The profile.** *The entry:* `entryPeople` up over the far tier, `entrySpeedPeople` fast, on the line from the rear to
 * the strip (the fleet's pass is drawn there: a point of fire). *The glide:* down a `glideDegrees` slope, the speed
 * bleeding evenly to `approachSpeedPeople` by `approachPeople` out — the ground track follows from the height and the slope
 * (400 people up at 12° is ~1,880 people, less the approach's 150). *The approach:* on the fans over `approachSeconds`,
 * from the slope's height at that point down to `flarePeople` over the strip, eased. *The flare:* straight down over
 * `flareSeconds`, touchdown. *On the ground:* the ramp over `rampSeconds`, the frames out one every `frameOutSeconds`
 * (step 2 makes them structures), `settleSeconds`, lift. *The ascent to orbit* (the owner's least-realistic worry — not
 * a steep climb): a powered climb over `ascentSeconds` back the way it came, the speed rising evenly to
 * `ascentSpeedPeople`, the pitch from `ascentPitch.start` easing to `.end` as it goes downrange, the fans handing over to
 * the bells — a table of the tick worked out once from the numbers (`ASCENT`), so the arc is pure; gone at `goneTick`.
 * The real planet gravity is nothing a seen fall could use (the crash's lesson), so the profile is kinematic by design,
 * every number the owner's dial. Off: no strip, no run, every byte as before.
 */
import type { GameState, LighterRun, Vec3, VehicleKind } from '../types';
import { LIGHTER, LIGHTER_ORDER, LIGHTER_UNLOAD } from '../constants/action';
import { VEHICLE_KINDS } from '../constants/body';
import { PHYS_DT } from '../constants/physics';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { personPointOn } from './fineTerrain';
import { dacos, dcos, dsin, dtan } from './dmath';
import { vecAdd, vecDot, vecLength, vecNormalize, vecScale, vecSub } from '../math';

const ticksOf = (seconds: number): number => Math.max(1, Math.round(seconds / PHYS_DT));
const ease = (x: number): number => { const t = Math.max(0, Math.min(1, x)); return t * t * (3 - 2 * t); };

/** A surface direction `dist` (world units) from `dir` along the tangent `t` on a planet of `radius`. */
function along(dir: Vec3, t: Vec3, dist: number, radius: number): Vec3 {
  const u = dist / radius;
  return vecNormalize(vecAdd(vecScale(dir, dcos(u)), vecScale(t, dsin(u))));
}
/** The tangent at `from` toward `to` (unit), or null when they coincide or oppose. */
function towardOn(from: Vec3, to: Vec3): Vec3 | null {
  const t = vecSub(to, vecScale(from, vecDot(from, to)));
  return vecLength(t) > 1e-12 ? vecNormalize(t) : null;
}

/**
 * The ascent's arc, worked out once from LIGHTER: per tick, the ground gone back along the line (people) and the height
 * (people), with the speed rising evenly to `ascentSpeedPeople` and the pitch easing from `ascentPitch.start` to `.end`.
 */
const ascentPitch = (tau: number): number => LIGHTER.ascentPitch.start + (LIGHTER.ascentPitch.end - LIGHTER.ascentPitch.start) * ease(Math.min(1, tau * LIGHTER.ascentSeconds / LIGHTER.pitchOverSeconds));   // (the climb angle's ease over pitchOverSeconds of the ascent's clock)
const ASCENT = (() => {
  const n = ticksOf(LIGHTER.ascentSeconds), x = new Float64Array(n + 1), h = new Float64Array(n + 1);
  let gx = 0, gh = 0;
  for (let k = 1; k <= n; k++) {
    const tau = k / n, v = LIGHTER.ascentSpeedPeople * tau, pitch = ascentPitch(tau);
    gx += v * dcos(pitch) * PHYS_DT; gh += v * dsin(pitch) * PHYS_DT;
    x[k] = gx; h[k] = gh;
  }
  // gone: the first tick past `farPeople` up (the far tier), else the table's end
  let gone = n;
  for (let k = 1; k <= n; k++) if (h[k] >= LIGHTER.farPeople) { gone = k; break; }
  return { n, x, h, gone };
})();
/** The pitch of the ascent `tau` (0 … 1) along (radians). */

/** The glide's ground track (people): from the entry to the approach point, by the entry's height and the slope. */
export function glideTrackPeople(): number {
  return LIGHTER.entryPeople / dtan(LIGHTER.glideDegrees * Math.PI / 180) - LIGHTER.approachPeople;
}



/**
 * 69.10 step 2: the load a run brings — the vehicle kinds the pool (63.1n's stock) is shortest of, each frame one of a kind
 * below its cap, the lowest share of its cap first (ties in VEHICLE_KINDS' order), round and round up to `frames`; empty
 * without a stock (a dry run), or with every kind at its cap.
 */
export function lighterLoad(state: Pick<GameState, 'vehicleStocks' | 'config'>, team: number, frames: number): VehicleKind[] {
  const stock = state.vehicleStocks?.[team];
  if (!stock) return [];
  const full = poolCeiling(state);
  const left: Partial<Record<VehicleKind, number>> = {};
  for (const k of VEHICLE_KINDS) { const e = stock[k]; if (e) left[k] = e.left; }
  const load: VehicleKind[] = [];
  for (let i = 0; i < frames; i++) {
    let pick: VehicleKind | null = null, best = Infinity;
    for (const k of VEHICLE_KINDS) {
      const e = stock[k]; if (!e || (left[k] ?? 0) >= full(e)) continue;
      const share = (left[k] ?? 0) / full(e);
      if (share < best) { best = share; pick = k; }
    }
    if (!pick) break;
    load.push(pick); left[pick] = (left[pick] ?? 0) + 1;
  }
  return load;
}

/** The pool's ceiling for a kind: the frames it started with — the stock's spares (`config.vehicleStock`) for every vehicle of the kind fielded (63.1n's `cap` counts the fielded). */
const poolCeiling = (state: Pick<GameState, 'config'>) => (e: { cap: number }): number => Math.max(1, e.cap * (state.config.vehicleStock ?? 1));

/** The enemy mounts this side has seen (core/mountIntel.ts's reading, kept here without its imports: this module sits under the ship body). */
function knownMountsOf(state: Pick<GameState, 'structures' | 'players' | 'mountsSeen'>, team: number): Array<{ planetIndex: number; surfaceDir: Vec3 }> {
  const seen = state.mountsSeen?.[team];
  if (!seen?.length) return [];
  return state.structures.filter(st => st.type === 'pointDefence' && seen.some(e => e.id === st.id) && state.players.find(p => p.id === st.ownerId)?.teamIndex !== team);   // (standing or not, as it believes: mountIntel drops a loss once seen)
}

/** The standing order: `auto` unless held. */
export const lighterOrder = (state: Pick<GameState, 'lighters'>, team: number): 'auto' | 'hold' => state.lighters?.order?.[team] ?? 'auto';
/** The side's standing order set. */
export function setLighterOrder(state: GameState, team: number, mode: 'auto' | 'hold'): GameState {
  const l = state.lighters ?? { runs: [], next: 0, issued: {} };
  return { ...state, lighters: { ...l, order: { ...(l.order ?? {}), [team]: mode } } };
}

/**
 * The standing order's beat (once a second, match.ts, with the switch): a side with a strip, not holding, no run up, its last
 * touchdown LIGHTER_ORDER.cadenceSeconds past (or none yet), and a vehicle kind's frames below the floor, calls a run with the
 * load the pool is shortest of (the commander's). The same state when nothing is due.
 */
export function stepLighterOrders(state: GameState, tick: number): GameState {
  if (!state.config.lighters || !state.lighterStrips) return state;
  let next = state;
  for (const team of Object.keys(state.lighterStrips).map(Number).sort((a, b) => a - b)) {
    if (lighterOrder(next, team) === 'hold' || lighterUp(next, team)) continue;
    // step 4: held while an enemy mount the side has seen stands within LIGHTER_ORDER.holdPeople of the strip (the approach is under its guns); a human can call anyway
    const strip = state.lighterStrips![team], planet = next.planets[strip.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(next).k;
    if (knownMountsOf(next, team).some(m => m.planetIndex === strip.planetIndex && dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(m.surfaceDir), vecNormalize(strip.dir))))) * planet.radius / person <= LIGHTER_ORDER.holdPeople)) continue;
    const last = next.lighters?.lastTouch?.[team];
    if (last !== undefined && tick - last < Math.round(LIGHTER_ORDER.cadenceSeconds / PHYS_DT)) continue;
    const stock = next.vehicleStocks?.[team];
    if (!stock || !VEHICLE_KINDS.some(k => { const e = stock[k]; return !!e && e.left < LIGHTER_ORDER.floor; })) continue;
    const r = callLighter(next, team, tick, { by: 'commander' });
    if (!('refused' in r)) next = r.state;
  }
  return next;
}


/** The side's frames staged (rolled out, not yet stacked) at `tick`, by kind. */
export function stagedFrames(state: Pick<GameState, 'lighters'>, team: number, tick: number): Partial<Record<VehicleKind, number>> {
  const out: Partial<Record<VehicleKind, number>> = {};
  for (const f of state.lighters?.staged?.[team] ?? []) if (f.stacked > tick) out[f.kind] = (out[f.kind] ?? 0) + 1;
  return out;
}

/** A run by its id, or null. */
export function lighterById(state: Pick<GameState, 'lighters'>, id: number): LighterRun | null {
  return state.lighters?.runs.find(r => r.id === id) ?? null;
}

/** The side's run in the air or on the ground now, or null. */
export function lighterUp(state: Pick<GameState, 'lighters'>, team: number): LighterRun | null {
  return state.lighters?.runs.find(r => r.team === team) ?? null;
}

/**
 * A lighter called down to the side's strip at `tick` with `frames` frames (LIGHTER.frames at most): its run planned — the
 * entry on the line from the rear, the glide's and approach's ticks from the speeds, the ground time from the frames, the
 * ascent. Refused without the switch, a strip, or with one already up.
 */
export function callLighter(state: GameState, team: number, tick: number, opts: { frames?: number; by?: 'human' | 'commander' } = {}): { state: GameState; run: LighterRun } | { refused: string } {
  if (!state.config.lighters) return { refused: 'No lighters this match' };
  const strip = state.lighterStrips?.[team];
  if (!strip) return { refused: 'No strip' };
  if (lighterUp(state, team)) return { refused: 'A lighter is up' };
  const planet = state.planets[strip.planetIndex], person = PERSON_BODY_HEIGHT * personSetOf(state).k;
  const frames = Math.max(1, Math.min(LIGHTER.frames, Math.round(opts.frames ?? LIGHTER.frames)));
  // step 2: the load (the kinds the pool is shortest of); a pool with every kind at its cap has no use for a run
  const load = lighterLoad(state, team, frames);
  if (state.vehicleStocks?.[team] && !load.length) return { refused: 'The pool is full' };
  const glidePeople = glideTrackPeople();
  const back = vecScale(strip.front, -1);
  const entry = along(vecNormalize(strip.dir), back, (glidePeople + LIGHTER.approachPeople) * person, planet.radius);
  const glideSeconds = glidePeople / ((LIGHTER.entrySpeedPeople + LIGHTER.approachSpeedPeople) / 2);
  const approachTick = tick + ticksOf(glideSeconds), flareTick = approachTick + ticksOf(LIGHTER.approachSeconds), touchTick = flareTick + ticksOf(LIGHTER.verticalSeconds);   // (STOVL: the hover's vertical descent after the approach)
  const rampTick = touchTick + ticksOf(LIGHTER.rampSeconds), unloadedTick = rampTick + frames * ticksOf(LIGHTER.frameOutSeconds), liftTick = unloadedTick + ticksOf(LIGHTER.settleSeconds);
  const goneTick = liftTick + ticksOf(LIGHTER.liftSeconds) + ASCENT.gone;   // (STOVL: the vertical lift before the pitch-over)
  const l = state.lighters ?? { runs: [], next: 0, issued: {} };
  const run: LighterRun = { id: l.next, team, planetIndex: strip.planetIndex, entry, strip: vecNormalize(strip.dir), front: strip.front, glidePeople, calledTick: tick, approachTick, flareTick, touchTick, rampTick, unloadedTick, liftTick, goneTick, frames, load, ...(opts.by ? { by: opts.by } : {}) };
  return { state: { ...state, lighters: { runs: [...l.runs, run], next: l.next + 1, issued: { ...l.issued, [team]: (l.issued[team] ?? 0) + 1 } } }, run };
}

export type LighterPhase = 'glide' | 'approach' | 'flare' | 'down' | 'ascent';
export interface LighterPose {
  /** Its belly's point in the world and the surface direction under it; its height over the ground there (world units). */
  pos: Vec3; dir: Vec3; height: number;
  /** The way it faces (a surface tangent at `dir`) and its pitch (radians, nose up positive). */
  heading: Vec3; pitch: number;
  /** Its speed over the ground (people a second) and the fans' and bells' shares (0 … 1). */
  speed: number; fans: number; bells: number;
  /** The ramp's share down, the frames aboard. */
  ramp: number; aboard: number;
  /** STOVL: the thrusters' articulation — 0 aft (the glide, the ascent's end), 1 straight down (the hover, the vertical descent and lift) — for the look's swinging nozzles. */
  nozzle: number;
  phase: LighterPhase;
}

/** A run's pose at `tick` (a pure function of it). Null before its call or once gone. */
export function lighterPose(state: Pick<GameState, 'planets' | 'config'>, r: LighterRun, tick: number): LighterPose | null {
  const planet = state.planets[r.planetIndex];
  if (!planet || tick < r.calledTick || tick >= r.goneTick) return null;
  const ps = personSetOf(state), person = PERSON_BODY_HEIGHT * ps.k, R = planet.radius, L = LIGHTER;
  const back = vecScale(r.front, -1), slope = dtan(L.glideDegrees * Math.PI / 180);
  const at = (dir: Vec3, heightPeople: number, heading: Vec3, pitch: number, speed: number, fans: number, bells: number, ramp: number, aboard: number, phase: LighterPhase, nozzle: number): LighterPose =>
    ({ pos: personPointOn(planet, dir, ps, heightPeople * person), dir, height: heightPeople * person, heading, pitch, speed, fans, bells, ramp, aboard, nozzle, phase });
  if (tick < r.approachTick) {
    // the glide: the ground gone from the entry with the speed bleeding evenly (d = T(v0 τ + (v1 − v0) τ² / 2)); the height down the slope
    const T = (r.approachTick - r.calledTick) * PHYS_DT, tau = (tick - r.calledTick) / (r.approachTick - r.calledTick);
    const v0 = L.entrySpeedPeople, v1 = L.approachSpeedPeople, gone = T * (v0 * tau + (v1 - v0) * tau * tau / 2), speed = v0 + (v1 - v0) * tau;
    const dir = along(r.entry, towardOn(r.entry, r.strip) ?? r.front, Math.min(gone, r.glidePeople) * person, R);   // (the great circle from the entry: its own tangent toward the strip)
    const height = L.entryPeople - gone * slope;
    return at(dir, Math.max(height, 0), r.front, -L.glideDegrees * Math.PI / 180, speed, 0, 0, 0, r.frames, 'glide', 0);
  }
  const hApproach = L.entryPeople - r.glidePeople * slope;
  if (tick < r.flareTick) {
    // the approach (STOVL): from the approach point to the strip, the thrusters swinging down as the speed bleeds to nothing, the height eased to the hover's, the nose coming level
    const tau = (tick - r.approachTick) / (r.flareTick - r.approachTick), e = ease(tau);
    const dir = along(r.strip, back, (1 - e) * L.approachPeople * person, R);
    const speed = L.approachSpeedPeople * (1 - tau);
    return at(dir, hApproach + (L.hoverPeople - hApproach) * e, r.front, -L.glideDegrees * Math.PI / 180 * (1 - e), speed, e, 0, 0, r.frames, 'approach', e);
  }
  if (tick < r.touchTick) {
    // the vertical descent: on the thrusters straight down, from the hover to the strip
    const tau = (tick - r.flareTick) / (r.touchTick - r.flareTick);
    return at(r.strip, L.hoverPeople * (1 - ease(tau)), r.front, 0, 0, 1, 0, 0, r.frames, 'flare', 1);
  }
  if (tick < r.liftTick) {
    const ramp = Math.max(0, Math.min(1, (tick - r.touchTick) / Math.max(1, r.rampTick - r.touchTick)));
    const out = tick < r.rampTick ? 0 : Math.min(r.frames, Math.floor((tick - r.rampTick) / ticksOf(L.frameOutSeconds)));
    return at(r.strip, 0, r.front, 0, 0, 0, 0, ramp, r.frames - out, 'down', 1);
  }
  // the take-off (STOVL; the owner: "takeoff flat and swivel thrusters to escape"): a vertical lift on the thrusters to liftPeople, then the ascent back the way it came — the table's arc, the climb angle the path's (straight up, easing downrange) with the hull near level (ascentHullPitch nose-up at most), the nozzles the thrust vector's angle (down at the climb's start, aft as it flattens) and the bells taking over as they reach aft
  const liftTicks = ticksOf(L.liftSeconds);
  if (tick < r.liftTick + liftTicks) {
    const tau = (tick - r.liftTick) / liftTicks;
    return at(r.strip, L.liftPeople * ease(tau), back, 0, 0, 1, 0, 0, 0, 'ascent', 1);
  }
  const k = Math.min(ASCENT.n, tick - r.liftTick - liftTicks), tau = k / ASCENT.n;   // (the climb and speed by the full ascent's clock; gone once past the far height)
  const dir = along(r.strip, back, ASCENT.x[k] * person, R);
  const climb = ascentPitch(tau), hull = L.ascentHullPitch * ease(Math.min(1, tau / 0.2));
  const nozzle = Math.max(0, Math.min(1, (climb - hull) / (Math.PI / 2)));   // (the thrust vector's angle over the hull: 1 straight down, 0 aft)
  const speed = L.ascentSpeedPeople * tau * dcos(climb);
  return at(dir, L.liftPeople + ASCENT.h[k], back, hull, speed, nozzle, 1 - nozzle, 0, 0, 'ascent', nozzle);
}

/**
 * The runs at `tick`: those to announce, those touching down now (the side's last touchdown noted), each frame rolling out
 * now (step 2: a frame of its kind onto the pool's stock, `vehicleStocks`, up to its cap — the robots' carry is step 3's),
 * those lifting now, and those gone (dropped).
 */
export function stepLighters(state: GameState, tick: number): { state: GameState; announced: LighterRun[]; down: LighterRun[]; unloaded: Array<{ run: LighterRun; kind: VehicleKind; left: number }>; lifted: LighterRun[]; gone: LighterRun[] } {
  const l = state.lighters;
  if (!l || (!l.runs.length && !l.staged)) return { state, announced: [], down: [], unloaded: [], lifted: [], gone: [] };   // (step 3: the staged frames are pruned after the run is gone too)
  const announced: LighterRun[] = [], down: LighterRun[] = [], lifted: LighterRun[] = [], gone: LighterRun[] = [], unloaded: Array<{ run: LighterRun; kind: VehicleKind; left: number }> = [];
  const runs: LighterRun[] = [];
  let lastTouch = l.lastTouch, stocks = state.vehicleStocks, changed = false;
  const frameTicks = ticksOf(LIGHTER.frameOutSeconds);
  // step 3: the staged frames — stacked ones pruned; a frame rolling out joins the queue behind the last (the robots carry one at a time)
  let staged = l.staged;
  if (staged) {
    const pruned: NonNullable<typeof staged> = {};
    let any = false, dropped = false;
    for (const [t, list] of Object.entries(staged)) { const keep = list.filter(f => f.stacked > tick); if (keep.length !== list.length) dropped = true; if (keep.length) { pruned[Number(t)] = keep; any = true; } }
    if (dropped) { staged = any ? pruned : undefined; changed = true; }
  }
  for (const r of l.runs) {
    if (tick >= r.goneTick) { gone.push(r); changed = true; continue; }
    let run = r;
    if (!run.announced) { run = { ...run, announced: true }; announced.push(run); changed = true; }
    if (tick === r.touchTick) { down.push(run); lastTouch = { ...(lastTouch ?? {}), [r.team]: tick }; changed = true; }
    // a frame out: the k-th rolls out frameOutSeconds after the (k−1)-th, the first after the ramp
    if (tick > r.rampTick && tick <= r.unloadedTick && (tick - r.rampTick) % frameTicks === 0) {
      const k = (tick - r.rampTick) / frameTicks - 1, kind = r.load[k];
      const e = kind ? stocks?.[r.team]?.[kind] : undefined;
      if (kind && e && e.left < poolCeiling(state)(e)) {
        stocks = { ...stocks, [r.team]: { ...stocks![r.team], [kind]: { ...e, left: e.left + 1 } } };
        unloaded.push({ run, kind, left: e.left + 1 }); changed = true;
        // the robots' carry: behind the frame before it, the pick, the walk and the stacking
        const queue = staged?.[r.team] ?? [], last = queue.length ? queue[queue.length - 1].stacked : tick;
        const lift = Math.max(tick, last), carry = state.lighterStrips?.[r.team]?.carryTicks ?? ticksOf(LIGHTER_UNLOAD.pickSeconds + LIGHTER_UNLOAD.stackSeconds);   // (the carry's ticks, worked out with the strip at the start: core/lighterStrip.ts)
        staged = { ...(staged ?? {}), [r.team]: [...queue, { kind, out: tick, lift, stacked: lift + carry }] };
      }
    }
    if (tick === r.liftTick) lifted.push(run);
    runs.push(run);
  }
  if (!changed) return { state, announced, down, unloaded, lifted, gone };
  const lighters = { ...l, runs, ...(lastTouch ? { lastTouch } : {}) };
  if (staged) lighters.staged = staged; else delete lighters.staged;
  return { state: { ...state, lighters, ...(stocks ? { vehicleStocks: stocks } : {}) }, announced, down, unloaded, lifted, gone };
}
